import type { Permission } from '@menage/shared';
import { CalendarRange, FileText, Home, House, LayoutDashboard, Map, MessageCircle, PiggyBank, Settings, Users, UsersRound, Wallet } from 'lucide-react';
import { lazy, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, type RouteObject } from 'react-router';
import { PortalShell, useUnreadMessages, type NavItem } from '@/components/shell';
import { useActor, useCan } from '@/lib/auth/AuthProvider';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PlanningPage = lazy(() => import('./pages/PlanningPage'));
const JobDetailPage = lazy(() => import('./pages/JobDetailPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const QuotesPage = lazy(() => import('./pages/QuotesPage'));
const QuoteDetailPage = lazy(() => import('./pages/QuoteDetailPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('./pages/EmployeeDetailPage'));
const EmployeeFormPage = lazy(() => import('./pages/EmployeeFormPage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const PayrollPage = lazy(() => import('./pages/PayrollPage'));
const FinancesPage = lazy(() => import('./pages/FinancesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

interface AdminNavItem extends NavItem {
  permission: Permission;
}

/** Staff portal. Supervisors get the SAME interface; items they cannot use disappear. */
function AdminLayout() {
  const { t } = useTranslation('admin');
  const can = useCan();
  const actor = useActor();
  const unread = useUnreadMessages(can('chat.read'));
  const all: AdminNavItem[] = [
    { to: '/admin', label: t('nav.home'), icon: LayoutDashboard, end: true, primary: true, permission: 'jobs.read' },
    { to: '/admin/planning', label: t('nav.planning'), icon: CalendarRange, primary: true, permission: 'jobs.read' },
    { to: '/admin/carte', label: t('nav.map'), icon: Map, permission: 'map.read' },
    { to: '/admin/soumissions', label: t('nav.quotes'), icon: FileText, primary: true, permission: 'quotes.read' },
    { to: '/admin/clients', label: t('nav.clients'), icon: Users, permission: 'clients.read' },
    { to: '/admin/proprietes', label: t('nav.properties'), icon: House, permission: 'properties.read' },
    { to: '/admin/employes', label: t('nav.employees'), icon: UsersRound, permission: 'employees.read' },
    { to: '/admin/equipes', label: t('nav.teams'), icon: Home, permission: 'teams.read' },
    { to: '/admin/paie', label: t('nav.payroll'), icon: Wallet, permission: 'payroll.read' },
    { to: '/admin/messages', label: t('nav.messages'), icon: MessageCircle, badge: unread, primary: true, permission: 'chat.read' },
    { to: '/admin/finances', label: t('nav.finances'), icon: PiggyBank, permission: 'finance.read' },
    { to: '/admin/parametres', label: t('nav.settings'), icon: Settings, permission: 'settings.read' },
  ];
  const nav = all.filter((i) => can(i.permission));
  return <PortalShell nav={nav} title={t(`common:roles.${actor.role}`)} homeTo="/admin" />;
}

/** Route-level permission gate (the API enforces it too). */
function Gate({ permission, children }: { permission: Permission; children: ReactElement }) {
  const can = useCan();
  return can(permission) ? children : <Navigate to="/admin" replace />;
}

const g = (permission: Permission, el: ReactElement) => <Gate permission={permission}>{el}</Gate>;

const adminRoutes: { element: ReactElement; children: RouteObject[] } = {
  element: <AdminLayout />,
  children: [
    { index: true, element: <DashboardPage /> },
    { path: 'planning', element: g('jobs.read', <PlanningPage />) },
    { path: 'menages/:id', element: g('jobs.read', <JobDetailPage />) },
    { path: 'carte', element: g('map.read', <MapPage />) },
    { path: 'soumissions', element: g('quotes.read', <QuotesPage />) },
    { path: 'soumissions/:id', element: g('quotes.read', <QuoteDetailPage />) },
    { path: 'clients', element: g('clients.read', <ClientsPage />) },
    { path: 'clients/:id', element: g('clients.read', <ClientDetailPage />) },
    { path: 'proprietes', element: g('properties.read', <PropertiesPage />) },
    { path: 'proprietes/:id', element: g('properties.read', <PropertyDetailPage />) },
    { path: 'messages', element: g('chat.read', <MessagesPage />) },
    { path: 'messages/:id', element: g('chat.read', <MessagesPage />) },
    { path: 'employes', element: g('employees.read', <EmployeesPage />) },
    { path: 'employes/nouveau', element: g('employees.manage', <EmployeeFormPage />) },
    { path: 'employes/:id', element: g('employees.read', <EmployeeDetailPage />) },
    { path: 'employes/:id/modifier', element: g('employees.manage', <EmployeeFormPage />) },
    { path: 'equipes', element: g('teams.read', <TeamsPage />) },
    { path: 'paie', element: g('payroll.read', <PayrollPage />) },
    { path: 'finances', element: g('finance.read', <FinancesPage />) },
    { path: 'parametres', element: g('settings.read', <SettingsPage />) },
    { path: 'parametres/:tab', element: g('settings.read', <SettingsPage />) },
  ],
};
export default adminRoutes;
